import { Injectable } from '@angular/core';
import { country } from '../enums/language';
import { ApiService } from './api.service';
import { EasyEatHqService } from './ee-hq.service';
import { LanguageService } from './language.service';
import { MessageService } from './message.service';
import { LocalStorageService } from './localStorage.service';
import { HttpHeaders } from '@angular/common/http';
import {
  CLIENT_ID,
  PAYMENT_DESCRIPTON,
  PAYMENT_METHOD,
} from 'src/app/collection-infrastructure/collection-infrastructure.enums';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  url: string = '';

  constructor(
    private _api: ApiService,
    private _lang: LanguageService,
    private _hqService: EasyEatHqService,
    private _messageService: MessageService,
    private _localStorage: LocalStorageService
  ) {}

  isPaymentInProgress = false; // Initialize the flag to indicate if payment is in progress

  async payNow(paymentDetails, reoccurChecked, options: any = {}) {
    if (this.isPaymentInProgress) {
      // If payment is already in progress, do not make another API call
      return;
    }

    try {
      this.isPaymentInProgress = true; // Set the flag to true to indicate payment in progress

      const domain = this._lang.getDomain();
      const paymentGateway = country[domain].paymentGateway;
      const show = country[domain].showPayment;
      const payload = {
        country_code: this._lang.getCountry(),
        payment_gateway: paymentGateway,
        payment_description:
          options.payment_description || PAYMENT_DESCRIPTON.NEGATIVE_BALANCE,
        client_id: options.client_id || CLIENT_ID.NEGATIVE_BALANCE,
        restaurant_data: paymentDetails,
        recurring_status: Boolean(reoccurChecked),
        is_hq: this._hqService.isHqUser(),
        ...(options.switch_to_lite && { switch_to_lite: true }),
        payment_method: options.payment_method || PAYMENT_METHOD.ONLINE,
        ...(options.selectedPlan && options.selectedPlan),
        ...(options.return_url && { return_url: options.return_url }),
      };

      const response = await this._api
        .initiateSettlementPayment(payload)
        .toPromise();

      if (((response || {}).data || {}).status === 1) {
        if (!this._hqService.isHqUser()) {
          this._localStorage.set(
            'invoice',
            ((((response || {}).data || {}).data || {}).script_data || {})
              .orderid || (((((response || {}).data || {}).data || {}).response || {}).order || {})
              .invoice_number
          );
        }
        this.url = (((response || {}).data || {}).data || {}).url;
        if (payload.country_code === country.ai.country && reoccurChecked) {
          const scriptData = (((response || {}).data || {}).data || {})
            .script_data;
          this.scriptClickButtonCreditOnly(scriptData);
        } else if (this.url) {
          this.openPaymentModal(show);
        }
      }
      return response;
    } catch (error) {
      console.error(error);
      this._messageService.showError(((error || {}).error || {}).message)
      // Implement error handling here (e.g., show a user-friendly error message)
    } finally {
      this.isPaymentInProgress = false; // Reset the flag to indicate payment is no longer in progress
    }
  }

  /**
   * Opens the payment modal to display the payment options.
   */
  openPaymentModal(show = true) {
    window.location.href = this.url;
  }

  /**
   * Restaurant dues: POST /payment/restaurant/initiate.
   * Stores payment_id and redirects to the gateway page when one is returned.
   */
  async initiateRestaurantDuesPayment(params: {
    restaurant_id: string;
    amount: number;
    country_code: string;
    payment_channel: string;
    redirect_url: string;
    bill_id?: string;
    payment_description?: string;
  }): Promise<any> {
    if (this.isPaymentInProgress) {
      return;
    }
    try {
      this.isPaymentInProgress = true;
      const response: any = await this._api
        .initiateRestaurantPayment(
          {
            restaurant_id: params.restaurant_id,
            amount: params.amount,
            country_code: params.country_code,
            payment_channel: params.payment_channel,
            redirect_url: params.redirect_url,
            ...(params.bill_id && { bill_id: params.bill_id }),
            payment_description:
              params.payment_description || PAYMENT_DESCRIPTON.NEGATIVE_BALANCE,
          },
          params.country_code
        )
        .toPromise();

      const payload = (response || {}).data || {};
      const paymentId = payload.payment_id;
      const pageUrl = payload.payment_page_url;
      if (paymentId) {
        this._localStorage.set('invoice', paymentId);
        localStorage.setItem('invoice', paymentId);
      }
      if (pageUrl) {
        window.location.href = pageUrl;
      }
      return response;
    } catch (error) {
      console.error(error);
      this._messageService.showError(
        ((error || {}).error || {}).message ||
          ((error || {}).error || {}).meta?.message ||
          'Could not start payment'
      );
      throw error;
    } finally {
      this.isPaymentInProgress = false;
    }
  }

  async recordRestaurantPayment(paymentId: string): Promise<any> {
    const response: any = await this._api
      .restaurantPaymentCallback({ payment_id: paymentId })
      .toPromise();
    return response;
  }

  async getRestaurantPaymentStatus(paymentId: string): Promise<any> {
    const response: any = await this._api
      .getRestaurantPaymentStatus(paymentId)
      .toPromise();
    return response;
  }

  async initiateOnlinePayment(params: any) {
    try {
      const fullResponse = await this._api.initiatePayment(params).toPromise();
      const safetyResponse = (fullResponse || {}).data || {};
      if (
        (safetyResponse.data || {}).qr_data ||
        safetyResponse.payment_page_url
      ) {
        return { paymentData: safetyResponse, fullResponse };
      } else {
        this._messageService.showError(
          fullResponse.meta.message || fullResponse.http.message
        );
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Opens the payment modal to display the qr options for kiosk.
   */
  async initiateKioskOnlinePayment(params: any) {
    try {
      const fullResponse = await this._api.initiateKioskPayment(params).toPromise();
      const safetyResponse = fullResponse?.data;
      if (
        safetyResponse?.data?.qr_data ||
        safetyResponse?.payment_page_url
      ) {
        return { paymentData: safetyResponse, fullResponse };
      } else {
        this._messageService.showError(
          fullResponse?.meta?.message || fullResponse?.http?.message
        );
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getRestaurantSettings(rest_id) {
    try {
      let reoccurChecked = true;
      // To avoid Disk cache call .
      const httpOptions = {
        headers: new HttpHeaders({
          'Cache-Control': 'no-cache',
        }),
      };
      const response = await this._api
        .getRestaurantSettings(rest_id, httpOptions)
        .toPromise();
      const safetyResponse = (response || {})['data'] || {};
      if (
        ((safetyResponse.settings || {}).global || {}).hasOwnProperty(
          'is_reoccuring_pmt'
        )
      ) {
        reoccurChecked = safetyResponse.settings.global.is_reoccuring_pmt;
      }
      return reoccurChecked;
    } catch (error) {
      return null;
    }
  }

  updateRestaurantSettings(restaurantId, reoccurChecked) {
    const payload = {
      restaurant_id: restaurantId,
      settings: {
        global: {
          is_reoccuring_pmt: reoccurChecked,
        },
      },
    };
    this._api.updateRestaurantSettings(payload).subscribe((response) => {});
  }

  scriptClickButtonCreditOnly(resp) {
    if (resp) {
      let razerpayButton = document.createElement('button');
      razerpayButton.setAttribute('data-toggle', 'molpayseamless');
      razerpayButton.setAttribute('hidden', 'true');
      razerpayButton.setAttribute('id', 'myPay');

      razerpayButton.setAttribute('data-mpsmerchantid', resp.merchantID);
      razerpayButton.setAttribute('data-mpschannel', resp.channel);
      razerpayButton.setAttribute('data-mpsamount', resp.amount);
      razerpayButton.setAttribute('data-mpsorderid', resp.orderid);
      razerpayButton.setAttribute('data-mpsbill_name', resp.bill_name);
      razerpayButton.setAttribute('data-mpsbill_email', resp.bill_email);
      razerpayButton.setAttribute('data-mpsbill_mobile', resp.bill_mobile);
      razerpayButton.setAttribute('data-mpsbill_desc', resp.bill_desc);
      razerpayButton.setAttribute('data-mpsvcode', resp.vcode);
      razerpayButton.setAttribute('data-mpsreturnurl', resp.returnurl);
      razerpayButton.setAttribute('data-mpscancelurl', resp.cancelurl);
      razerpayButton.setAttribute('data-mpstokenstatus', '1');
      const body = document.getElementsByTagName('body');
      body[0].appendChild(razerpayButton);
      razerpayButton.click();
      razerpayButton.remove();
    }
  }
}
